import { useMutation } from '@apollo/client';
import {
    ADD_COURSE_LEARNING_OBJECT,
    DELETE_COURSE_LEARNING_OBJECT,
    ENABLE_COURSE_LEARNING_OBJECT,
    DISABLE_COURSE_LEARNING_OBJECT,
    SORT_COURSE_LEARNING_OBJECTS,
} from '../mutations/LEARNING_OBJECT_COURSE_MUTATIONS';

/**
 * Hook for learning-object manipulation on courses
 * Add, Remove, Enable, Disable, and Save Order
 * NOTE: at one point we had an issue on BE where performing many of these operations at 
 *  once overloaded it and broke things. Since then that may have been fixed but the guy 
 *  who did it was laid off so we dont actually know. Dont do parallel queries without 
 *  extensive testing first.
 * @param {UUID} courseId 
 * @returns 
 */
const useLearningObjectCourseMutations = ( courseId ) => {
    const [ addLearningObject ] = useMutation( ADD_COURSE_LEARNING_OBJECT );
    const [ deleteLearningObject ] = useMutation( DELETE_COURSE_LEARNING_OBJECT );
    const [ enableLearningObject ] = useMutation( ENABLE_COURSE_LEARNING_OBJECT );
    const [ disableLearningObject ] = useMutation( DISABLE_COURSE_LEARNING_OBJECT );
    const [ sortObjects ] = useMutation( SORT_COURSE_LEARNING_OBJECTS );

    async function addLearningObjects( ids ) {
        return ids.reduce( async ( accPromise, id ) => {
            const response = await accPromise;

            try {
                const res = await addLearningObject( { variables: { courseID: courseId, learningObjectID: id } } );
                if ( !res.data.result.success ) {
                    response.success = false;
                    response.errors.push( `Could not add learning object ${ id } to course ${ courseId }` );
                }
            } catch ( e ) {
                console.error( e );
                response.success = false;
                response.errors.push( `Could not add learning object ${ id } to course ${ courseId }` );
            }

            return response;
        }, Promise.resolve( { success: true, errors: [] } ) );
    }

    async function deleteLearningObjects( ids ) {
        return ids.reduce( async ( accPromise, id ) => {
            const response = await accPromise;

            try {
                const res = await deleteLearningObject( { variables: { courseID: courseId, learningObjectID: id } } );
                if ( !res.data.result.success ) {
                    response.success = false;
                    response.errors.push( `Could not delete learning object ${ id } from course ${ courseId }` );
                }
            } catch ( e ) {
                console.error( e );
                response.success = false;
                response.errors.push( `Could not delete learning object ${ id } from course ${ courseId }` );
            }

            return response;
        }, Promise.resolve( { success: true, errors: [] } ) );
    }

    async function enableLearningObjects( ids ) {
        return ids.reduce( async ( accPromise, id ) => {
            const response = await accPromise;

            try {
                const res = await enableLearningObject( { variables: { courseID: courseId, learningObjectID: id } } );
                if ( !res.data.result.success ) {
                    response.success = false;
                    response.errors.push( `Could not enable learning object ${ id } on course ${ courseId }` );
                }
            } catch ( e ) {
                console.error( e );
                response.success = false;
                response.errors.push( `Could not enable learning object ${ id } on course ${ courseId }` );
            }

            return response;
        }, Promise.resolve( { success: true, errors: [] } ) );
    }

    async function disableLearningObjects( ids ) {
        return ids.reduce( async ( accPromise, id ) => {
            const response = await accPromise;

            try {
                const res = await disableLearningObject( { variables: { courseID: courseId, learningObjectID: id } } );
                if ( !res.data.result.success ) {
                    response.success = false;
                    response.errors.push( `Could not disable learning object ${ id } on course ${ courseId }` );
                }
            } catch ( e ) {
                console.error( e );
                response.success = false;
                response.errors.push( `Could not disable learning object ${ id } on course ${ courseId }` );
            }

            return response;
        }, Promise.resolve( { success: true, errors: [] } ) );
    }

    async function sortLearningObjects( ids ) {
        const response = {
            success: true,
            errors: [],
        };

        try {
            const res = await sortObjects( { variables: { input: { courseID: courseId, learningObjectIDs: ids } } } );
            if ( !res.data.result.success ) {
                response.success = false;
                response.errors.push( `Could not sort learning objects on course ${ courseId }` );
            }
        } catch ( e ) {
            console.error( e );
            response.success = false;
            response.errors.push( `Could not sort learning objects on course ${ courseId }` );
        }

        return response;
    }

    return {
        addLearningObjects,
        deleteLearningObjects,
        enableLearningObjects,
        disableLearningObjects,
        sortLearningObjects,
    };
};

export default useLearningObjectCourseMutations;
